import { cn } from '../../utils/cn';

export interface SkillLevelBadgeProps {
  /** Skill rating 1.0–7.0 per the BraveMove skill model. */
  readonly value: number;
  /** Render the detailed radial dial instead of the compact pill. */
  readonly variant?: 'pill' | 'radial';
  readonly size?: 'sm' | 'md' | 'lg';
  readonly className?: string;
  /**
   * F31 Decision 11 — confidence gate. When `'provisional'` (fewer than the
   * minimum-N confirmed matches), the badge renders a coarse "PROVISIONAL"
   * treatment with NO numeric value, regardless of `value`. Defaults to
   * `'confirmed'` (numeric shown) so every pre-F31 call site is unaffected.
   */
  readonly confidenceTier?: 'provisional' | 'confirmed';
}

const SIZE_PILL = {
  sm: 'h-5 px-1.5 text-[10px]',
  md: 'h-6 px-2 text-[11px]',
  lg: 'h-7 px-2.5 text-[12.5px]',
} as const;

const SIZE_RADIAL = { sm: 32, md: 44, lg: 56 } as const;

function clamp(v: number): number {
  return Math.max(1, Math.min(7, v));
}

function toneFor(v: number): { bg: string; ring: string; text: string } {
  // 1-2 → muted, 3-4 → info, 5 → success, 6 → warning (advanced), 7 → accent (elite)
  if (v >= 7) return { bg: 'bg-accent', ring: 'stroke-accent', text: 'text-accent-ink' };
  if (v >= 6) return { bg: 'bg-warning', ring: 'stroke-warning', text: 'text-ink' };
  if (v >= 5) return { bg: 'bg-success', ring: 'stroke-success', text: 'text-accent-ink' };
  if (v >= 3) return { bg: 'bg-info', ring: 'stroke-info', text: 'text-accent-ink' };
  return { bg: 'bg-muted', ring: 'stroke-muted', text: 'text-ink' };
}

export function SkillLevelBadge({
  value,
  variant = 'pill',
  size = 'md',
  className,
  confidenceTier = 'confirmed',
}: SkillLevelBadgeProps) {
  const v = clamp(value);
  const tone = toneFor(v);

  // F31 Decision 11 — provisional players show a coarse label, not the number.
  if (confidenceTier === 'provisional') {
    if (variant === 'pill') {
      return (
        <span
          aria-label="Skill level provisional — not enough confirmed matches yet"
          className={cn(
            'inline-flex items-center gap-1 rounded-full font-bold bg-muted text-ink',
            SIZE_PILL[size],
            className,
          )}
        >
          <span className="opacity-70 uppercase tracking-wider text-[0.85em]">SKL</span>
          <span className="uppercase tracking-wide text-[0.85em]">Provisional</span>
        </span>
      );
    }
    const pxP = SIZE_RADIAL[size];
    const rP = (pxP - 4) / 2;
    const cxP = pxP / 2;
    return (
      <span
        role="img"
        aria-label="Skill level provisional — not enough confirmed matches yet"
        className={cn('relative inline-flex items-center justify-center', className)}
        style={{ width: pxP, height: pxP }}
      >
        <svg width={pxP} height={pxP} viewBox={`0 0 ${pxP} ${pxP}`} className="absolute inset-0">
          <circle
            cx={cxP}
            cy={cxP}
            r={rP}
            className="stroke-muted"
            strokeWidth={3}
            strokeDasharray="3 3"
            fill="none"
          />
        </svg>
        <span
          className="relative text-ink opacity-60 font-bold leading-none"
          style={{ fontSize: pxP * 0.32 }}
        >
          ?
        </span>
      </span>
    );
  }

  if (variant === 'pill') {
    return (
      <span
        aria-label={`Skill level ${v.toFixed(1)} of 7.0`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full font-bold tabular-nums',
          tone.bg,
          tone.text,
          SIZE_PILL[size],
          className,
        )}
      >
        <span className="opacity-70 uppercase tracking-wider text-[0.85em]">SKL</span>
        <span>{v.toFixed(1)}</span>
      </span>
    );
  }
  const px = SIZE_RADIAL[size];
  const r = (px - 4) / 2;
  const cx = px / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - v / 7);
  return (
    <span
      role="img"
      aria-label={`Skill level ${v.toFixed(1)} of 7.0`}
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: px, height: px }}
    >
      <svg width={px} height={px} viewBox={`0 0 ${px} ${px}`} className="absolute inset-0">
        <circle cx={cx} cy={cx} r={r} className="stroke-hairline" strokeWidth={3} fill="none" />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          className={tone.ring}
          strokeWidth={3}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${cx} ${cx})`}
        />
      </svg>
      <span
        className="relative text-ink font-bold tabular-nums leading-none"
        style={{ fontSize: px * 0.32 }}
      >
        {v.toFixed(1)}
      </span>
    </span>
  );
}
